if (!customElements.get('bundle-builder-bmc')) {
  customElements.define(
    'bundle-builder-bmc',
    class BundleBuilderBMC extends HTMLElement {
      constructor() {
        super();
        this.productId = this.dataset.productId;
        this.sectionId = this.dataset.section;
        this.variantData = JSON.parse(this.querySelector('[data-variants-json]').textContent);
        this.productData = JSON.parse(this.querySelector('[data-product-json]').textContent);

        this.tierInputs = this.querySelectorAll('.bundle-bmc__tier-input');
        this.variantSlotsContainer = this.querySelector('.bundle-bmc__variant-slots');
        this.infoContainer = this.querySelector('.bundle-bmc__info');
        this.totalPriceEl = this.querySelector('.bundle-bmc__total-price');
        this.totalCompareEl = this.querySelector('[data-total-compare]');
        this.totalSavingsEl = this.querySelector('[data-total-savings]');
        this.totalDiscountBadgeEl = this.querySelector('[data-total-discount-badge]');
        this.totalContainer = this.querySelector('.bundle-bmc__total');
        this.submitWrapper = this.querySelector('.bundle-bmc__submit-wrapper');
        this.submitBtn = this.querySelector('.bundle-bmc__submit-wrapper .button');
        this.submitHint = this.querySelector('[data-submit-hint]');
        this.customOrderMessage = this.querySelector('.bundle-bmc__custom-order-message');
        this.cartPreview = this.querySelector('[data-cart-preview]');
        this.cartItemCountEl = this.querySelector('[data-cart-item-count]');
        this.cartItemsContainer = this.querySelector('[data-cart-items]');
        this.whatsappOrderButton = this.querySelector('[data-whatsapp-order]');
        this.errorWrapper = this.querySelector('.bundle-bmc__error-message-wrapper');
        this.mainImage = this.querySelector('.bundle-bmc__main-image');
        this.thumbButtons = this.querySelectorAll('.bundle-bmc__thumb');

        this.selectedTier = 1;
        this.selectedVariants = [];
        this.customTitles = [];
        this.activeSlotIndex = 0;
        this.quickAddMode = false;
        this.quickAddPending = false;
        this.cartTotals = {
          original: 0,
          final: 0,
          discount: 0,
          currency: null,
          itemCount: 0,
          discountLabel: ''
        };
        this.cachedCartItems = [];

        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);

        this.init();
      }

      init() {
        this.ensureVariantSlotsContainer();

        this.tierInputs.forEach((input) => {
          input.addEventListener('change', this.onTierChange.bind(this));
        });

        if (this.submitBtn) {
          this.submitBtn.addEventListener('click', this.onSubmit.bind(this));
        }

        if (this.whatsappOrderButton) {
          this.whatsappOrderButton.addEventListener('click', this.onWhatsappOrder.bind(this));
        }

        const defaultTier = this.querySelector('.bundle-bmc__tier-input:checked');
        if (defaultTier) {
          this.selectedTier = parseInt(defaultTier.value, 10);
          this.updateTierVisuals(this.selectedTier);
        }

        this.updateCustomOrderState();

        this.thumbButtons.forEach((button) => {
          button.addEventListener('click', this.onThumbClick.bind(this));
        });

        if (this.cartItemsContainer) {
          this.cartItemsContainer.addEventListener('click', (event) => {
            const removeButton = event.target.closest('[data-remove-line]');
            const addButton = event.target.closest('[data-add-placeholder]');
            if (addButton) {
              const slotIndex = parseInt(addButton.dataset.placeholderIndex || '0', 10);
              this.openQuickAdd(Number.isNaN(slotIndex) ? 0 : slotIndex);
              return;
            }

            if (!removeButton) return;
            const line = parseInt(removeButton.dataset.removeLine, 10);
            if (!Number.isNaN(line)) {
              this.removeCartLine(line);
            }
          });
        }

        if (this.variantSlotsContainer) {
          this.variantSlotsContainer.addEventListener('input', (event) => {
            if (event.target && event.target.closest('.bmc-custom-template-selector')) {
              this.updateTotal();
            }
          });
          this.variantSlotsContainer.addEventListener('change', (event) => {
            if (event.target && event.target.closest('.bmc-custom-template-selector')) {
              this.updateTotal();
            }
          });
        }

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);

        this.refreshCartTotals().finally(() => {
          this.applyTierFromCart();
          this.renderVariantSlots();
          this.updateTotal();
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
      }

      handleDocumentClick(event) {
        if (!this.quickAddMode || this.selectedTier === 4) return;

        const clickTarget = event.target;
        if (!clickTarget || !(clickTarget instanceof Element)) return;

        if (this.contains(clickTarget) && clickTarget.closest('[data-add-placeholder]')) return;
        if (this.variantSlotsContainer && this.variantSlotsContainer.contains(clickTarget)) return;

        this.closeQuickAdd();
      }

      handleDocumentKeydown(event) {
        if (!this.quickAddMode || this.selectedTier === 4) return;
        if (event.key === 'Escape') {
          this.closeQuickAdd();
        }
      }

      closeQuickAdd() {
        if (!this.quickAddMode) return;
        this.quickAddMode = false;
        this.quickAddPending = false;
        this.updateCustomOrderState();
      }

      ensureVariantSlotsContainer() {
        if (this.variantSlotsContainer) return;

        const container = document.createElement('div');
        container.className = 'bundle-bmc__variant-slots';
        container.setAttribute('aria-live', 'polite');

        if (this.cartPreview && this.cartPreview.parentNode) {
          this.cartPreview.insertAdjacentElement('afterend', container);
        } else if (this.infoContainer) {
          this.infoContainer.appendChild(container);
        } else {
          this.appendChild(container);
        }

        this.variantSlotsContainer = container;
      }

      async refreshCartTotals(options = {}) {
        const { syncTier = true } = options;
        try {
          const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
          const response = await fetch(`${root}cart.js`, {
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          if (!response.ok) return;

          const cart = await response.json();
          const discountApplications = Array.isArray(cart.cart_level_discount_applications)
            ? cart.cart_level_discount_applications
            : [];

          this.cartTotals = {
            original: cart.original_total_price || cart.total_price || 0,
            final: cart.total_price || 0,
            discount: cart.total_discount || 0,
            currency: cart.currency || null,
            itemCount: cart.item_count || 0,
            discountLabel: discountApplications.map((item) => item.title).filter(Boolean).join(', ')
          };

          this.cachedCartItems = Array.isArray(cart.items) ? cart.items : [];

          this.renderCartItems(cart);
          if (syncTier) {
            this.applyTierFromCart();
          }
        } catch (error) {
          this.cartTotals = {
            original: 0,
            final: 0,
            discount: 0,
            currency: null,
            itemCount: 0,
            discountLabel: ''
          };

          this.cachedCartItems = [];

          this.renderCartItems({ items: [], item_count: 0 });
        }
      }

      renderCartItems(cart) {
        if (!this.cartItemsContainer || !this.cartItemCountEl) return;

        const items = Array.isArray(cart.items) ? cart.items : [];
        const count = items.length;
        const checkedTierInput = this.querySelector('.bundle-bmc__tier-input:checked');
        const selectedTierValue = checkedTierInput
          ? parseInt(checkedTierInput.value, 10)
          : Number(this.selectedTier || 1);
        const placeholdersNeeded = selectedTierValue >= 4 ? 0 : Math.max(selectedTierValue - count, 0);

        this.cartItemCountEl.textContent = String(count);
        this.cartItemsContainer.innerHTML = '';

        items.forEach((item, index) => {
          const slot = document.createElement('div');
          slot.className = 'bundle-bmc__cart-slot bundle-bmc__cart-slot--filled';

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'bundle-bmc__cart-remove';
          remove.dataset.removeLine = String(index + 1);
          remove.setAttribute('aria-label', `Remove ${item.product_title || item.title || 'item'} from cart`);
          remove.textContent = '×';

          const imageWrap = document.createElement('div');
          imageWrap.className = 'bundle-bmc__cart-image-wrap';

          if (item.image) {
            const image = document.createElement('img');
            image.className = 'bundle-bmc__cart-image';
            image.src = item.image;
            image.alt = item.product_title || item.title || 'Cart item';
            image.loading = 'lazy';
            imageWrap.appendChild(image);
          } else {
            const noImage = document.createElement('span');
            noImage.className = 'bundle-bmc__cart-image-fallback';
            noImage.textContent = '+';
            imageWrap.appendChild(noImage);
          }

          slot.appendChild(remove);
          slot.appendChild(imageWrap);
          this.cartItemsContainer.appendChild(slot);
        });

        if (placeholdersNeeded > 0) {
          for (let i = 0; i < placeholdersNeeded; i++) {
            const placeholder = document.createElement('div');
            placeholder.className = 'bundle-bmc__cart-slot bundle-bmc__cart-slot--empty';

            const addButton = document.createElement('button');
            addButton.type = 'button';
            addButton.className = 'bundle-bmc__cart-slot-add';
            addButton.dataset.addPlaceholder = 'true';
            addButton.dataset.placeholderIndex = String(count + i);
            addButton.setAttribute('aria-label', `Add item ${count + i + 1}`);

            const plus = document.createElement('span');
            plus.className = 'bundle-bmc__cart-plus';
            plus.textContent = '+';

            addButton.appendChild(plus);
            placeholder.appendChild(addButton);
            this.cartItemsContainer.appendChild(placeholder);
          }
        }

      }

      async removeCartLine(line) {
        try {
          const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
          const response = await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ line, quantity: 0 })
          });

          if (!response.ok) return;

          const cart = await response.json();

          this.cartTotals = {
            original: cart.original_total_price || cart.total_price || 0,
            final: cart.total_price || 0,
            discount: cart.total_discount || 0,
            currency: cart.currency || null,
            itemCount: cart.item_count || 0,
            discountLabel: (cart.cart_level_discount_applications || []).map((item) => item.title).filter(Boolean).join(', ')
          };
          this.cachedCartItems = Array.isArray(cart.items) ? cart.items : [];

          this.renderCartItems(cart);
          this.applyTierFromCart();
          this.renderVariantSlots();
          this.updateTotal();
        } catch (error) {
          this.showError('Unable to remove item from cart.');
        }
      }

      applyTierFromCart() {
        const itemCount = Number(this.cartTotals.itemCount || 0);
        if (itemCount <= 0) return;

        const autoTier = itemCount >= 4 ? 4 : itemCount;
        const targetInput = this.querySelector(`#bundle-tier-${this.sectionId}-${autoTier}`);

        if (targetInput) {
          targetInput.checked = true;
          this.selectedTier = autoTier;
          this.updateTierVisuals(this.selectedTier);
          this.updateCustomOrderState();
          this.emitVariantChange();
        }
      }

      onThumbClick(evt) {
        const button = evt.currentTarget;
        if (!button || !this.mainImage) return;

        const mainSrc = button.dataset.mainSrc;
        const alt = button.dataset.alt || this.mainImage.alt;

        if (mainSrc) {
          this.mainImage.src = mainSrc;
          this.mainImage.removeAttribute('srcset');
          this.mainImage.removeAttribute('sizes');
          this.mainImage.alt = alt;
        }

        this.thumbButtons.forEach((thumb) => {
          thumb.classList.remove('is-active');
        });
        button.classList.add('is-active');
      }

      updateTierVisuals(selectedValue) {
        this.tierInputs.forEach((input) => {
          const label = this.querySelector(`label[for="${input.id}"]`);
          if (label) {
            if (parseInt(input.value, 10) === selectedValue) {
              label.classList.add('selected');
            } else {
              label.classList.remove('selected');
            }
          }
        });
      }

      onTierChange(evt) {
        this.selectedTier = parseInt(evt.target.value, 10);
        this.selectedVariants = [];
        this.customTitles = [];
        this.activeSlotIndex = 0;
        this.quickAddMode = false;
        this.updateTierVisuals(this.selectedTier);
        this.updateCustomOrderState();
        this.renderCartItems({ items: this.cachedCartItems, item_count: this.cachedCartItems.length });
        this.emitVariantChange();
        this.renderVariantSlots();
        this.updateTotal();
        this.refreshCartTotals({ syncTier: false }).finally(() => {
          this.updateTotal();
        });
      }

      emitVariantChange() {
        this.dispatchEvent(
          new CustomEvent('BMC_VARIANT_CHANGE', {
            bubbles: true,
            detail: {
              sectionId: this.sectionId,
              tier: this.selectedTier,
              tierLabel: this.getSlotTitle(Math.max(this.selectedTier - 1, 0)),
              isCustomOrder: this.selectedTier === 4
            }
          })
        );
      }

      updateCustomOrderState() {
        const isCustomOrderTier = this.selectedTier === 4;

        if (this.submitWrapper) {
          if (isCustomOrderTier) {
            this.submitWrapper.setAttribute('hidden', '');
          } else {
            this.submitWrapper.removeAttribute('hidden');
          }
        }

        if (this.totalContainer) {
          if (isCustomOrderTier) {
            this.totalContainer.setAttribute('hidden', '');
          } else {
            this.totalContainer.removeAttribute('hidden');
          }
        }

        if (this.variantSlotsContainer) {
          if (isCustomOrderTier) {
            this.variantSlotsContainer.setAttribute('hidden', '');
          } else {
            if (this.quickAddMode) {
              this.variantSlotsContainer.removeAttribute('hidden');
            } else {
              this.variantSlotsContainer.setAttribute('hidden', '');
            }
          }
        }

        if (this.customOrderMessage) {
          if (isCustomOrderTier) {
            this.customOrderMessage.removeAttribute('hidden');
          } else {
            this.customOrderMessage.setAttribute('hidden', '');
          }
        }

        if (this.cartPreview) {
          if (isCustomOrderTier) {
            this.cartPreview.setAttribute('hidden', '');
          } else {
            this.cartPreview.removeAttribute('hidden');
          }
        }

        if (isCustomOrderTier) {
          this.hideError();
          if (this.submitHint) {
            this.submitHint.setAttribute('hidden', '');
            this.submitHint.textContent = '';
          }
        }
      }

      renderVariantSlots() {
        if (!this.variantSlotsContainer) return;

        if (this.selectedTier === 4) {
          this.variantSlotsContainer.innerHTML = '';
          return;
        }

        const fragment = document.createDocumentFragment();

        if (this.quickAddMode) {
          const slotIndex = Math.min(Math.max(this.activeSlotIndex, 0), Math.max(this.selectedTier - 1, 0));
          const currentVariant = this.selectedVariants[slotIndex];
          const slot = this.createVariantSlot(slotIndex, currentVariant, true);
          fragment.appendChild(slot);
          this.variantSlotsContainer.innerHTML = '';
          this.variantSlotsContainer.appendChild(fragment);
          return;
        }

        for (let i = 0; i < this.selectedTier; i++) {
          const currentVariant = this.selectedVariants[i];
          const slot = this.createVariantSlot(i, currentVariant, i === this.activeSlotIndex);
          fragment.appendChild(slot);
        }

        this.variantSlotsContainer.innerHTML = '';
        this.variantSlotsContainer.appendChild(fragment);
      }

      createVariantSlot(index, currentVariant, shouldOpen) {
        const slot = document.createElement('div');
        slot.className = 'bundle-bmc__variant-slot';
        slot.dataset.slotIndex = index;

        let resolvedVariant = currentVariant;
        if (!resolvedVariant) {
          resolvedVariant = this.variantData.find((v) => v.available) || this.variantData[0];
          this.selectedVariants[index] = resolvedVariant;
        }

        this.activeSlotIndex = shouldOpen ? index : this.activeSlotIndex;

        const headerWrap = document.createElement('div');
        headerWrap.className = 'bundle-bmc__slot-summary';

        const header = document.createElement('div');
        header.className = 'bundle-bmc__variant-slot-header';

        const title = document.createElement('span');
        title.className = 'bundle-bmc__variant-slot-title';
        title.textContent = `Book ${index + 1}`;

        header.appendChild(title);
        const summaryMain = document.createElement('div');
        summaryMain.className = 'bundle-bmc__slot-summary-main';

        if (resolvedVariant && resolvedVariant.featured_image) {
          const summaryImage = document.createElement('img');
          summaryImage.className = 'bundle-bmc__slot-summary-image';
          summaryImage.src = resolvedVariant.featured_image.src;
          summaryImage.alt = resolvedVariant.title;
          summaryImage.loading = 'lazy';
          summaryMain.appendChild(summaryImage);
        }

        const summaryText = document.createElement('div');
        summaryText.className = 'bundle-bmc__slot-summary-text';
        summaryText.appendChild(header);

        if (resolvedVariant) {
          const selectedLabel = document.createElement('span');
          selectedLabel.className = 'bundle-bmc__slot-selected';
          selectedLabel.textContent = resolvedVariant.title;
          summaryText.appendChild(selectedLabel);
        }

        summaryMain.appendChild(summaryText);
        headerWrap.appendChild(summaryMain);
        slot.appendChild(headerWrap);

        const content = document.createElement('div');
        content.className = 'bundle-bmc__slot-content';

        const titlePropertyGroup = document.createElement('div');
        titlePropertyGroup.className = 'bundle-bmc__line-property';

        const titlePropertyLabel = document.createElement('label');
        titlePropertyLabel.className = 'bundle-bmc__line-property-label';
        titlePropertyLabel.setAttribute('for', `bundle-line-title-${this.sectionId}-${index}`);
        titlePropertyLabel.textContent = 'Title *';

        const titlePropertyInput = document.createElement('input');
        titlePropertyInput.type = 'text';
        titlePropertyInput.id = `bundle-line-title-${this.sectionId}-${index}`;
        titlePropertyInput.className = 'bundle-bmc__line-property-input';
        titlePropertyInput.placeholder = 'Enter title';
        titlePropertyInput.required = true;
        titlePropertyInput.value = this.customTitles[index] || '';
        titlePropertyInput.dataset.slotIndex = index;
        titlePropertyInput.addEventListener('input', this.onTitleInput.bind(this));

        titlePropertyGroup.appendChild(titlePropertyLabel);
        titlePropertyGroup.appendChild(titlePropertyInput);
        content.appendChild(titlePropertyGroup);

        const templatePropertyGroup = document.createElement('div');
        templatePropertyGroup.className = 'bundle-bmc__line-property bundle-bmc__line-property--app';

        const templatePropertyLabel = document.createElement('label');
        templatePropertyLabel.className = 'bundle-bmc__line-property-label';
        templatePropertyLabel.textContent = 'Template *';

        const templateMount = document.createElement('div');
        templateMount.className = 'bmc-custom-template-selector';
        templateMount.id = `bmc-custom-template-selector-${this.sectionId}-${index}`;
        templateMount.dataset.slotIndex = String(index);

        templatePropertyGroup.appendChild(templatePropertyLabel);
        templatePropertyGroup.appendChild(templateMount);
        content.appendChild(templatePropertyGroup);

        this.productData.options.forEach((option, optIdx) => {
          const group = document.createElement('div');
          group.className = 'bundle-bmc__option-group product-form__input product-form__input--pill';

          const isVisualOption = /size/i.test(option);
          if (isVisualOption) {
            group.classList.add('product-form__input--bmc-visual');
          }

          const label = document.createElement('label');
          label.className = 'form__label bundle-bmc__option-label';
          label.textContent = option;
          group.appendChild(label);

          const valuesWrap = document.createElement('div');
          valuesWrap.className = 'bundle-bmc__option-values';
          valuesWrap.setAttribute('role', 'radiogroup');
          valuesWrap.setAttribute('aria-label', option);

          const optionValues = this.getUniqueOptionValues(optIdx);
          const selectedValue = resolvedVariant
            ? resolvedVariant.options[optIdx]
            : optionValues[0];

          optionValues.forEach((value) => {
            const isUnavailable = this.isOptionValueUnavailable(optIdx, value, index, selectedValue);

            const input = document.createElement('input');
            input.type = 'radio';
            input.name = `bundle-option-${index}-${optIdx}`;
            input.value = value;
            input.className = 'bundle-bmc__option-input';
            input.id = `bundle-option-${this.sectionId}-${index}-${optIdx}-${value}`;
            if (value === selectedValue && !isUnavailable) {
              input.checked = true;
            }
            if (isUnavailable) {
              input.disabled = true;
              input.classList.add('disabled');
            }
            input.dataset.slotIndex = index;
            input.dataset.optionIndex = optIdx;

            const labelBtn = document.createElement('label');
            labelBtn.className = 'bundle-bmc__option-label-btn';
            labelBtn.setAttribute('for', input.id);

            if (isVisualOption) {
              const optionImage = this.getOptionImage(optIdx, value);
              if (optionImage) {
                labelBtn.classList.add('variant-pill-label--with-image');

                const mediaWrap = document.createElement('span');
                mediaWrap.className = 'variant-pill__media';

                const image = document.createElement('img');
                image.src = optionImage;
                image.alt = value;
                image.loading = 'lazy';
                mediaWrap.appendChild(image);

                const text = document.createElement('span');
                text.className = 'variant-pill__text';
                text.textContent = value;

                labelBtn.appendChild(mediaWrap);
                labelBtn.appendChild(text);
              } else {
                labelBtn.textContent = value;
              }
            } else {
              labelBtn.textContent = value;
            }

            if (isUnavailable) {
              labelBtn.classList.add('bundle-bmc__option-label-btn--disabled');
            }
            if (value === selectedValue && !isUnavailable) {
              labelBtn.classList.add('bundle-bmc__option-label-btn--selected');
            }

            input.addEventListener('change', this.onOptionChange.bind(this));

            valuesWrap.appendChild(input);
            valuesWrap.appendChild(labelBtn);
          });

          group.appendChild(valuesWrap);
          content.appendChild(group);
        });

        const quickAddAction = document.createElement('div');
        quickAddAction.className = 'bundle-bmc__quick-add-action';

        const quickAddButton = document.createElement('button');
        quickAddButton.type = 'button';
        quickAddButton.className = 'button bundle-bmc__quick-add-button';
        quickAddButton.dataset.slotIndex = String(index);
        quickAddButton.textContent = 'Add this book';
        quickAddButton.addEventListener('click', this.onQuickAddButtonClick.bind(this));

        quickAddAction.appendChild(quickAddButton);
        content.appendChild(quickAddAction);

        slot.appendChild(content);

        return slot;
      }

      async onQuickAddButtonClick(evt) {
        const slotIndex = parseInt(evt.currentTarget.dataset.slotIndex, 10);
        if (Number.isNaN(slotIndex)) return;

        const variant = this.selectedVariants[slotIndex];
        const customTitle = (this.customTitles[slotIndex] || '').trim();

        if (!variant) {
          this.showError(`Please select options for Book ${slotIndex + 1}.`);
          return;
        }

        if (!customTitle) {
          this.showError(`Please enter Title for Book ${slotIndex + 1}.`);
          return;
        }

        if (!this.hasRequiredSlotProperties(slotIndex)) {
          this.showError(`Please complete template selection for Book ${slotIndex + 1}.`);
          return;
        }

        this.hideError();
        await this.tryQuickAddFromSlot(slotIndex);
      }

      getFieldPropertyKey(field) {
        if (!field || !field.name) return null;
        const match = field.name.match(/^properties\[(.+)\]$/);
        if (match) return match[1];
        if (field.dataset.lineItemProperty === 'true') {
          return field.dataset.lineItemPropertyKey || field.name;
        }
        return null;
      }

      collectSlotLineItemProperties(slotIndex) {
        if (!this.variantSlotsContainer) return {};

        const slot = this.variantSlotsContainer.querySelector(`[data-slot-index="${slotIndex}"]`);
        if (!slot) return {};

        const fields = slot.querySelectorAll('.bmc-custom-template-selector input[name], .bmc-custom-template-selector select[name], .bmc-custom-template-selector textarea[name]');
        const properties = {};

        fields.forEach((field) => {
          if (field.disabled) return;
          if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;

          const key = this.getFieldPropertyKey(field);
          if (!key) return;

          if (key.toLowerCase() === 'title') return;

          const value = (field.value || '').trim();
          if (value === '') return;

          properties[key] = value;
        });

        return properties;
      }

      hasRequiredSlotProperties(slotIndex) {
        if (!this.variantSlotsContainer) return false;

        const slot = this.variantSlotsContainer.querySelector(`[data-slot-index="${slotIndex}"]`);
        if (!slot) return false;

        const selector = slot.querySelector('.bmc-custom-template-selector');
        if (!selector) return false;

        const templateIdField = selector.querySelector('input[name="properties[template_id]"], input[name="properties[templateId]"]');
        if (templateIdField && String(templateIdField.value || '').trim() !== '') {
          return true;
        }

        const templateDataFields = selector.querySelectorAll('input[name^="properties[template_"]');
        for (const field of templateDataFields) {
          if (String(field.value || '').trim() !== '') {
            return true;
          }
        }

        const selectedTrigger = selector.querySelector('.pbs-template-selector__trigger.is-selected');
        if (selectedTrigger) {
          return true;
        }

        const templateNameEl = selector.querySelector('.pbs-template-selector__name, .pbs-template-selector__title');
        if (templateNameEl) {
          const selectedName = String(templateNameEl.textContent || '').trim();
          if (selectedName && selectedName.toLowerCase() !== 'no template selected') {
            return true;
          }
        }

        const selectorText = String(selector.textContent || '').toLowerCase();
        if (selectorText.includes('template id:') && !selectorText.includes('no template selected')) {
          return true;
        }

        return false;
      }

      getOptionImage(optionIndex, value) {
        const variant = this.variantData.find((item) => item.options[optionIndex] === value && item.featured_image);
        return variant && variant.featured_image ? variant.featured_image.src : null;
      }

      getSlotTitle(index) {
        const titles = [
          window.bundleBmcStrings?.tierLabel_1 || 'Item 1',
          window.bundleBmcStrings?.tierLabel_2 || 'Item 2',
          window.bundleBmcStrings?.tierLabel_3 || 'Item 3',
          window.bundleBmcStrings?.tierLabel_4 || 'Item 4'
        ];
        return titles[index] || `Item ${index + 1}`;
      }

      onTitleInput(evt) {
        const slotIndex = parseInt(evt.target.dataset.slotIndex, 10);
        this.customTitles[slotIndex] = evt.target.value;
        this.updateTotal();
      }

      getUniqueOptionValues(optionIndex) {
        const values = this.variantData.map((v) => v.options[optionIndex]);
        return [...new Set(values)];
      }

      isOptionValueUnavailable(optionIndex, value, slotIndex, selectedValue) {
        const currentSelections = this.getCurrentOptionValues(slotIndex);
        currentSelections[optionIndex] = value;

        const match = this.variantData.find((variant) => {
          return variant.options.every((opt, idx) => opt === currentSelections[idx]);
        });

        return !match || !match.available;
      }

      getCurrentOptionValues(slotIndex) {
        const slot = this.variantSlotsContainer.querySelector(`[data-slot-index="${slotIndex}"]`);
        if (!slot) {
          const firstVariant = this.variantData.find((v) => v.available) || this.variantData[0];
          return [...firstVariant.options];
        }

        const values = this.productData.options.map((_, optIdx) => {
          const checked = slot.querySelector(`input[name="bundle-option-${slotIndex}-${optIdx}"]:checked`);
          return checked ? checked.value : null;
        });

        return values;
      }

      onOptionChange(evt) {
        const slotIndex = parseInt(evt.target.dataset.slotIndex, 10);
        this.activeSlotIndex = slotIndex;

        const selectedValues = this.getCurrentOptionValues(slotIndex);

        const variant = this.variantData.find((v) => {
          return v.options.every((opt, idx) => opt === selectedValues[idx]);
        });

        if (variant) {
          this.selectedVariants[slotIndex] = variant;
        }

        this.updateSlotSummary(slotIndex);
        this.updateTotal();
        this.refreshCartTotals({ syncTier: false }).finally(() => {
          this.updateTotal();
        });
      }

      openQuickAdd(slotIndex = 0) {
        this.quickAddMode = true;
        this.quickAddPending = false;
        if (this.variantSlotsContainer) {
          this.variantSlotsContainer.removeAttribute('hidden');
        }

        const maxSlotIndex = Math.max(this.selectedTier - 1, 0);
        this.activeSlotIndex = Math.min(Math.max(slotIndex, 0), maxSlotIndex);
        this.updateCustomOrderState();
        this.renderVariantSlots();
        this.updateTotal();

        if (this.variantSlotsContainer) {
          this.variantSlotsContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }

      async tryQuickAddFromSlot(slotIndex) {
        if (this.quickAddPending) return;

        const variant = this.selectedVariants[slotIndex];
        const customTitle = (this.customTitles[slotIndex] || '').trim();
        if (!variant || !customTitle || !this.hasRequiredSlotProperties(slotIndex)) return;

        const properties = this.collectSlotLineItemProperties(slotIndex);
        properties._bundle_item = this.getSlotTitle(slotIndex);
        properties.Title = customTitle;

        this.quickAddPending = true;

        const success = await this.addItemsToCart([
          {
            id: Number(variant.id),
            quantity: 1,
            properties
          }
        ], { openCartUi: false });
        this.quickAddPending = false;

        if (!success) return;

        this.quickAddMode = false;
        this.customTitles = [];
        this.selectedVariants = [];
        this.activeSlotIndex = 0;
        this.updateCustomOrderState();
        this.renderVariantSlots();
        this.refreshCartTotals().finally(() => {
          this.updateCustomOrderState();
          this.renderVariantSlots();
          this.updateTotal();
        });
      }

      getTierDiscountLabel(tier) {
        const fallbackByTier = {
          1: '0%',
          2: '10%',
          3: '15%',
          4: '20%'
        };
        const key = `tierDiscount_${tier}`;
        const configuredLabel = window.bundleBmcStrings && window.bundleBmcStrings[key]
          ? String(window.bundleBmcStrings[key]).trim()
          : '';

        if (configuredLabel) return configuredLabel;

        return fallbackByTier[tier] || '0%';
      }

      updateSlotSummary(slotIndex) {
        const slot = this.variantSlotsContainer.querySelector(`[data-slot-index="${slotIndex}"]`);
        if (!slot) return;

        const variant = this.selectedVariants[slotIndex];
        if (!variant) return;

        const summaryLabel = slot.querySelector('.bundle-bmc__slot-selected');
        if (summaryLabel) {
          summaryLabel.textContent = variant.title;
        }

        const summaryImage = slot.querySelector('.bundle-bmc__slot-summary-image');
        if (summaryImage && variant.featured_image && variant.featured_image.src) {
          summaryImage.src = variant.featured_image.src;
          summaryImage.alt = variant.title;
        }
      }

      updateTotal() {
        if (!this.totalPriceEl) return;

        if (this.selectedTier === 4) {
          if (this.submitBtn) {
            this.submitBtn.disabled = true;
          }
          if (this.submitHint) {
            this.submitHint.setAttribute('hidden', '');
            this.submitHint.textContent = '';
          }
          return;
        }

        const checkedTier = this.querySelector('.bundle-bmc__tier-input:checked');
        const tierCount = checkedTier ? parseInt(checkedTier.value, 10) : this.selectedTier;
        this.selectedTier = Number.isNaN(tierCount) ? this.selectedTier : tierCount;

        let bundleFinal = 0;
        let allSelected = true;
        let blockMessage = '';

        for (let i = 0; i < this.selectedTier; i++) {
          let variant = this.selectedVariants[i];
          if (!variant) {
            variant = this.variantData.find((v) => v.available) || this.variantData[0];
            this.selectedVariants[i] = variant;
          }

          if (variant) {
            const variantFinal = variant.price || 0;
            bundleFinal += variantFinal;
          } else {
            allSelected = false;
            if (!blockMessage) {
              blockMessage = `Please select options for Book ${i + 1}.`;
            }
          }

          const currentTitle = (this.customTitles[i] || '').trim();
          if (currentTitle === '') {
            allSelected = false;
            if (!blockMessage) {
              blockMessage = `Please enter Title for Book ${i + 1}.`;
            }
          }

          if (!this.hasRequiredSlotProperties(i)) {
            allSelected = false;
            if (!blockMessage) {
              blockMessage = `Please complete template selection for Book ${i + 1}.`;
            }
          }
        }

        const hasExistingCartItems = (this.cartTotals.itemCount || 0) > 0;
        const fullFinal = Number(this.cartTotals.final || 0);
        const fullOriginal = Number(this.cartTotals.original || fullFinal);
        const tierDiscountLabel = this.getTierDiscountLabel(this.selectedTier);

        this.totalPriceEl.textContent = this.formatMoney(fullFinal);

        if (this.totalCompareEl) {
          if (hasExistingCartItems && this.cartTotals.discount > 0 && fullOriginal > fullFinal) {
            this.totalCompareEl.textContent = this.formatMoney(fullOriginal);
            this.totalCompareEl.removeAttribute('hidden');
          } else {
            this.totalCompareEl.setAttribute('hidden', '');
          }
        }

        if (this.totalSavingsEl) {
          const savings = hasExistingCartItems ? Math.max(this.cartTotals.discount || 0, 0) : 0;
          if (savings > 0) {
            this.totalSavingsEl.textContent = `Cart discount applied: ${this.formatMoney(savings)}`;
            this.totalSavingsEl.removeAttribute('hidden');
          } else {
            this.totalSavingsEl.setAttribute('hidden', '');
          }
        }

        if (this.totalDiscountBadgeEl) {
          if (tierDiscountLabel) {
            this.totalDiscountBadgeEl.textContent = tierDiscountLabel;
            this.totalDiscountBadgeEl.removeAttribute('hidden');
          } else {
            this.totalDiscountBadgeEl.setAttribute('hidden', '');
          }
        }

        if (this.submitBtn) {
          this.submitBtn.disabled = !allSelected;
        }

        if (this.submitHint) {
          if (!allSelected && this.selectedTier !== 4) {
            this.submitHint.textContent = blockMessage || 'Please complete all required fields.';
            this.submitHint.removeAttribute('hidden');
          } else {
            this.submitHint.setAttribute('hidden', '');
            this.submitHint.textContent = '';
          }
        }
      }

      async onSubmit(evt) {
        evt.preventDefault();

        if (!this.submitBtn || this.submitBtn.disabled || this.selectedTier === 4) return;

        this.hideError();
        this.submitBtn.classList.add('loading');
        this.submitBtn.disabled = true;

        const items = [];

        for (let i = 0; i < this.selectedTier; i++) {
          const variant = this.selectedVariants[i];
          const customTitle = (this.customTitles[i] || '').trim();

          if (!customTitle) {
            this.showError(`Please enter Title for Book ${i + 1}.`);
            this.submitBtn.classList.remove('loading');
            this.submitBtn.disabled = false;
            return;
          }

          if (!this.hasRequiredSlotProperties(i)) {
            this.showError(`Please complete template selection for Book ${i + 1}.`);
            this.submitBtn.classList.remove('loading');
            this.submitBtn.disabled = false;
            return;
          }

          if (variant) {
            const properties = this.collectSlotLineItemProperties(i);
            properties._bundle_item = this.getSlotTitle(i);
            properties.Title = customTitle;

            items.push({
              id: Number(variant.id),
              quantity: 1,
              properties
            });
          }
        }

        if (items.length === 0) {
          this.submitBtn.classList.remove('loading');
          this.submitBtn.disabled = false;
          return;
        }

        const success = await this.addItemsToCart(items, { openCartUi: true });
        if (success) {
          this.customTitles = [];
          this.selectedVariants = [];
          this.activeSlotIndex = 0;
          this.renderVariantSlots();
        }

        this.submitBtn.classList.remove('loading');
        this.submitBtn.disabled = false;
      }

      async addItemsToCart(items, options = {}) {
        const { openCartUi = true } = options;
        try {
          const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ items })
          });

          const responseText = await response.text();
          let data = {};

          try {
            data = responseText ? JSON.parse(responseText) : {};
          } catch (parseError) {
            data = { message: responseText };
          }

          if (!response.ok || data.status) {
            let errorMessage = data.description || data.message;

            if (!errorMessage && data.errors) {
              if (typeof data.errors === 'string') {
                errorMessage = data.errors;
              } else {
                const firstError = Object.values(data.errors)[0];
                errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
              }
            }

            this.showError(errorMessage || 'Could not add items to cart.');
            return false;
          }

          await this.refreshCartTotals();
          this.updateTotal();

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bundle-builder-bmc',
              cartData: data
            });
          }

          if (openCartUi) {
            const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(data);
            } else if (window.routes?.cart_url) {
              window.location = window.routes.cart_url;
            }
          }

          return true;
        } catch (error) {
          this.showError(error && error.message ? error.message : 'An error occurred. Please try again.');
          return false;
        }
      }

      getLinePropertyValue(item, keys) {
        const properties = item && item.properties ? item.properties : {};
        for (const key of keys) {
          if (!Object.prototype.hasOwnProperty.call(properties, key)) continue;
          const value = String(properties[key] || '').trim();
          if (value) return value;
        }
        return '';
      }

      getItemOptionValue(item, keys) {
        const options = Array.isArray(item && item.options_with_values) ? item.options_with_values : [];
        for (const option of options) {
          const name = String(option && option.name ? option.name : '').trim().toLowerCase();
          if (!name) continue;
          for (const key of keys) {
            if (name === key.toLowerCase()) {
              const value = String(option.value || '').trim();
              if (value) return value;
            }
          }
        }
        return '';
      }

      buildWhatsappMessage(cart) {
        const lines = [];
        const customerName = this.whatsappOrderButton
          ? String(this.whatsappOrderButton.dataset.customerName || '').trim()
          : '';

        lines.push('Hi, I want to order this product.');
        lines.push(`Name: ${customerName || 'Guest'}`);

        const items = Array.isArray(cart && cart.items) ? cart.items : [];
        if (items.length > 0) {
          lines.push('');
          lines.push('Line items:');

          items.forEach((item, index) => {
            const pages = this.getLinePropertyValue(item, ['Pages', 'pages']) || this.getItemOptionValue(item, ['pages']);
            const format = this.getLinePropertyValue(item, ['Format', 'format', 'Size', 'size']) || this.getItemOptionValue(item, ['format', 'size']);
            const title = this.getLinePropertyValue(item, ['Title', 'title']);
            const templateName = this.getLinePropertyValue(item, ['template_name', 'template_title', 'template']);
            const templateImgUrl = this.getLinePropertyValue(item, ['template_img_url', 'template_image_url', 'template_url']);

            lines.push(`${index + 1}. ${item.product_title || item.title || 'Item'}`);
            lines.push(`- Title: ${title || 'N/A'}`);
            lines.push(`- Pages: ${pages || 'N/A'}`);
            lines.push(`- Format: ${format || 'N/A'}`);
            lines.push(`- template_name: ${templateName || 'N/A'}`);
            lines.push(`- template_img_url: ${templateImgUrl || 'N/A'}`);
          });
        }

        if (Number(cart && cart.total_discount ? cart.total_discount : 0) > 0) {
          const discountTitle = Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications[0]
            ? String(cart.cart_level_discount_applications[0].title || '').trim()
            : '';
          const discountLabel = discountTitle || 'Discount';
          lines.push('');
          lines.push(`${discountLabel} (-${this.formatMoney(cart.total_discount)})`);
        }

        lines.push('Estimated total');
        lines.push(this.formatMoney(cart && cart.total_price ? cart.total_price : 0));

        return lines.join('\n');
      }

      async onWhatsappOrder() {
        if (!this.whatsappOrderButton) return;

        const rawPhone = String(this.whatsappOrderButton.dataset.whatsappPhone || '').trim();
        const phone = rawPhone.replace(/[^0-9]/g, '');
        if (!phone) {
          this.showError('WhatsApp number is missing.');
          return;
        }

        try {
          const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
          const response = await fetch(`${root}cart.js`, {
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          if (!response.ok) {
            this.showError('Unable to prepare WhatsApp message right now.');
            return;
          }

          const cart = await response.json();
          const message = this.buildWhatsappMessage(cart);
          const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

          window.location.href = whatsappUrl;
        } catch (error) {
          this.showError('Unable to prepare WhatsApp message right now.');
        }
      }

      showError(message) {
        if (!this.errorWrapper) return;
        const errorMsg = this.errorWrapper.querySelector('.bundle-bmc__error-message');
        if (errorMsg) errorMsg.textContent = message;
        this.errorWrapper.removeAttribute('hidden');
      }

      hideError() {
        if (!this.errorWrapper) return;
        this.errorWrapper.setAttribute('hidden', '');
      }

      formatMoney(cents) {
        const rawValue = Number(cents || 0);
        const value = Number.isNaN(rawValue) ? 0 : rawValue / 100;
        const currency = this.cartTotals && this.cartTotals.currency ? this.cartTotals.currency : 'INR';

        try {
          return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency,
            maximumFractionDigits: 2
          }).format(value);
        } catch (error) {
          return `₹${value.toFixed(2)}`;
        }
      }
    }
  );
}

document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('.bundle-bmc__thumbs').forEach((el) => {
    if (typeof Swiper === 'undefined') return;
    if (el.swiper) return;

    const sectionId = el.dataset.section;

    new Swiper(el, {
      loop: false,
      watchOverflow: true,
      slidesPerView: 'auto',
      spaceBetween: 10,
      navigation: {
        nextEl: `.bundle-bmc__thumbs-next--${sectionId}`,
        prevEl: `.bundle-bmc__thumbs-prev--${sectionId}`
      },
      breakpoints: {
        0: {
          slidesPerView: 'auto',
          spaceBetween: 8
        },
        750: {
          slidesPerView: 'auto',
          spaceBetween: 12
        },
        990: {
          slidesPerView: 'auto',
          spaceBetween: 12
        }
      }
    });
  });
});
